import { supabase } from '@/lib/supabase';
import { createScopedLogger } from '@/utils/logging/createScopedLogger';

const logger = createScopedLogger('SupabaseDedup');
const logWithTimestamp = (msg: string, ...args: unknown[]) => logger.info(msg, ...args);
const errorWithTimestamp = (msg: string, ...args: unknown[]) => logger.error(msg, ...args);
import {
  userProfileCacheService,
  CachedUserProfile,
  CachedGamification,
  CachedNotificationPreferences,
} from './userProfileCacheService';
import { logoutSignalService } from './logoutSignalService';

type RequestKey = string;

interface InFlightRequest<T> {
  promise: Promise<T>;
  timestamp: number;
}

const REQUEST_TIMEOUT_MS = 30000;

class SupabaseRequestDeduplicator {
  private inFlightRequests: Map<RequestKey, InFlightRequest<unknown>> = new Map();

  private generateKey(table: string, operation: string, params: Record<string, unknown>): RequestKey {
    const sortedParams = Object.keys(params)
      .sort()
      .map(k => `${k}:${JSON.stringify(params[k])}`)
      .join('|');
    return `${table}:${operation}:${sortedParams}`;
  }

  private cleanupStaleRequests(): void {
    const now = Date.now();
    for (const [key, request] of this.inFlightRequests.entries()) {
      if (now - request.timestamp > REQUEST_TIMEOUT_MS) {
        this.inFlightRequests.delete(key);
        logWithTimestamp(`🔄 [DEDUP] Cleaned up stale request: ${key}`);
      }
    }
  }

  private async deduplicateRequest<T>(
    key: RequestKey,
    fetchFn: () => Promise<T>
  ): Promise<T> {
    if (logoutSignalService.isLoggingOut) {
      logWithTimestamp(`🔄 [DEDUP] Request blocked (logout in progress): ${key}`);
      return null as T;
    }

    this.cleanupStaleRequests();

    const existing = this.inFlightRequests.get(key);
    if (existing) {
      logWithTimestamp(`🔄 [DEDUP] Request coalesced: ${key}`);
      return existing.promise as Promise<T>;
    }

    logWithTimestamp(`🔄 [DEDUP] New request started: ${key}`);
    const promise = fetchFn().finally(() => {
      this.inFlightRequests.delete(key);
    });

    this.inFlightRequests.set(key, {
      promise,
      timestamp: Date.now(),
    });

    return promise;
  }

  async fetchUserProfile(
    smartuserId: string,
    options: { forceRefresh?: boolean } = {}
  ): Promise<CachedUserProfile | null> {
    if (!options.forceRefresh) {
      const cached = userProfileCacheService.getUserProfile(smartuserId);
      if (cached) {
        logWithTimestamp(`👤 [DEDUP] Using cached profile for ${smartuserId}`);
        return cached;
      }
    }

    const key = this.generateKey('users', 'select', { smartuser_id: smartuserId });

    return this.deduplicateRequest(key, async () => {
      logWithTimestamp(`👤 [DEDUP] Fetching profile from DB for ${smartuserId}`);

      const { data, error } = await supabase
        .from('users')
        .select('smartuser_id, email, phone_number, pseudo, date_of_birth, profile_picture_url, is_paying, created_at')
        .eq('smartuser_id', smartuserId)
        .maybeSingle();

      if (error) {
        errorWithTimestamp(`👤 [DEDUP] Error fetching profile:`, error);
        return null;
      }

      if (!data) {
        logWithTimestamp(`👤 [DEDUP] No profile found for ${smartuserId}`);
        return null;
      }

      const profile: CachedUserProfile = {
        smartuserId: data.smartuser_id,
        email: data.email,
        phone: data.phone_number,
        pseudo: data.pseudo,
        birthday: data.date_of_birth,
        profilePictureUrl: data.profile_picture_url,
        isPaying: data.is_paying === true,
        createdAt: data.created_at,
        timestamp: Date.now(),
      };

      userProfileCacheService.setUserProfile(profile);
      return profile;
    });
  }

  async fetchGamification(
    smartuserId: string,
    options: { forceRefresh?: boolean } = {}
  ): Promise<CachedGamification | null> {
    if (!options.forceRefresh) {
      const cached = userProfileCacheService.getGamification(smartuserId);
      if (cached) {
        logWithTimestamp(`🎮 [DEDUP] Using cached gamification for ${smartuserId}`);
        return cached;
      }
    }

    const key = this.generateKey('user_gamification', 'select', { smartuser_id: smartuserId });

    return this.deduplicateRequest(key, async () => {
      logWithTimestamp(`🎮 [DEDUP] Fetching gamification from DB for ${smartuserId}`);

      const { data, error } = await supabase
        .from('user_gamification')
        .select('smartuser_id, total_coins, consecutive_days_streak, last_visit_date')
        .eq('smartuser_id', smartuserId)
        .maybeSingle();

      if (error) {
        errorWithTimestamp(`🎮 [DEDUP] Error fetching gamification:`, error);
        return null;
      }

      if (!data) {
        logWithTimestamp(`🎮 [DEDUP] No gamification record found for ${smartuserId}`);
        return null;
      }

      const gamification: CachedGamification = {
        smartuserId: data.smartuser_id,
        totalCoins: data.total_coins,
        consecutiveDaysStreak: data.consecutive_days_streak,
        lastVisitDate: data.last_visit_date,
        timestamp: Date.now(),
      };

      userProfileCacheService.setGamification(gamification);
      return gamification;
    });
  }

  async fetchNotificationPreferences(
    smartuserId: string | null,
    options: { forceRefresh?: boolean } = {}
  ): Promise<CachedNotificationPreferences | null> {
    if (!options.forceRefresh) {
      const cached = userProfileCacheService.getNotificationPreferences(smartuserId);
      if (cached) {
        logWithTimestamp(`🔔 [DEDUP] Using cached notification prefs for ${smartuserId || 'anonymous'}`);
        return cached;
      }
    }

    const key = this.generateKey('user_notification_preferences', 'select', {
      smartuser_id: smartuserId || 'null',
    });

    return this.deduplicateRequest(key, async () => {
      logWithTimestamp(`🔔 [DEDUP] Fetching notification prefs from DB for ${smartuserId || 'anonymous'}`);

      let query = supabase
        .from('user_notification_preferences')
        .select('new_episodes, recommendations, subscription_reminders, rewards, marketing');

      if (smartuserId) {
        query = query.eq('smartuser_id', smartuserId);
      } else {
        query = query.is('smartuser_id', null);
      }

      const { data, error } = await query.maybeSingle();

      if (error && error.code !== 'PGRST116') {
        errorWithTimestamp(`🔔 [DEDUP] Error fetching notification prefs:`, error);
        return null;
      }

      const defaultPrefs = {
        newEpisodes: true,
        recommendations: true,
        subscriptionReminders: true,
        rewards: true,
        marketing: false,
      };

      const prefs: CachedNotificationPreferences = {
        smartuserId,
        newEpisodes: data?.new_episodes ?? defaultPrefs.newEpisodes,
        recommendations: data?.recommendations ?? defaultPrefs.recommendations,
        subscriptionReminders: data?.subscription_reminders ?? defaultPrefs.subscriptionReminders,
        rewards: data?.rewards ?? defaultPrefs.rewards,
        marketing: data?.marketing ?? defaultPrefs.marketing,
        timestamp: Date.now(),
      };

      userProfileCacheService.setNotificationPreferences(prefs);
      return prefs;
    });
  }

  async fetchAllUserData(
    smartuserId: string,
    options: { forceRefresh?: boolean } = {}
  ): Promise<{
    profile: CachedUserProfile | null;
    gamification: CachedGamification | null;
    notificationPreferences: CachedNotificationPreferences | null;
  }> {
    logWithTimestamp(`🔄 [DEDUP] Fetching all user data for ${smartuserId} (forceRefresh: ${options.forceRefresh})`);

    const [profile, gamification, notificationPreferences] = await Promise.all([
      this.fetchUserProfile(smartuserId, options),
      this.fetchGamification(smartuserId, options),
      this.fetchNotificationPreferences(smartuserId, options),
    ]);

    if (options.forceRefresh) {
      userProfileCacheService.setLastRefreshTimestamp();
    }

    logWithTimestamp(`🔄 [DEDUP] All user data fetched for ${smartuserId}`);

    return {
      profile,
      gamification,
      notificationPreferences,
    };
  }

  invalidateUserCache(smartuserId: string): void {
    userProfileCacheService.clearUserData(smartuserId);
    logWithTimestamp(`🔄 [DEDUP] User cache invalidated for ${smartuserId}`);
  }

  invalidateGamificationCache(smartuserId: string): void {
    userProfileCacheService.invalidateGamification(smartuserId);
    logWithTimestamp(`🔄 [DEDUP] Gamification cache invalidated for ${smartuserId}`);
  }

  getInFlightRequestCount(): number {
    return this.inFlightRequests.size;
  }

  clearAllInFlightRequests(): void {
    this.inFlightRequests.clear();
    logWithTimestamp('🔄 [DEDUP] All in-flight requests cleared');
  }
}

export const supabaseRequestDeduplicator = new SupabaseRequestDeduplicator();
